import React, { useMemo, useState } from "react";
import { motion } from "motion/react";
import {
  History,
  FileText,
  CheckCircle2,
  Edit,
  Trash2,
  Plus,
  Clock,
} from "lucide-react";


// office_hub.css 5113 to 5524


export default function TaskHistoryTimelineSection() {
  const [selectedFilter, setSelectedFilter] = useState("all");

  const timelineEntries = [
    {
      id: 1,
      user: "Danish",
      avatar: "👨‍💼",
      action: "updated",
      field: "Status",
      oldValue: "To Do",
      newValue: "In Progress",
      timestamp: "2024-12-10 14:30",
      timeAgo: "2 hours ago",
    },
    {
      id: 2,
      user: "Sarah",
      avatar: "👩‍💼",
      action: "commented",
      comment: "Great progress! Let's make sure we hit the deadline this Friday.",
      timestamp: "2024-12-10 13:15",
      timeAgo: "3 hours ago",
    },
    {
      id: 3,
      user: "Mohammad",
      avatar: "👨",
      action: "updated",
      field: "Due Date",
      oldValue: "08-Dec-2024",
      newValue: "10-Dec-2024",
      timestamp: "2024-12-10 11:45",
      timeAgo: "5 hours ago",
    },
    {
      id: 4,
      user: "Danish",
      avatar: "👨‍💼",
      action: "updated",
      field: "Assignee",
      oldValue: "Unassigned",
      newValue: "Danish",
      timestamp: "2024-12-10 09:20",
      timeAgo: "7 hours ago",
    },
    {
      id: 5,
      user: "Sarah",
      avatar: "👩‍💼",
      action: "commented",
      comment: "Added initial requirements doc to the files section.",
      timestamp: "2024-12-09 16:30",
      timeAgo: "1 day ago",
    },
    {
      id: 6,
      user: "Mohammad",
      avatar: "👨",
      action: "updated",
      field: "Priority",
      oldValue: "Medium",
      newValue: "High",
      timestamp: "2024-12-09 14:10",
      timeAgo: "1 day ago",
    },
    {
      id: 7,
      user: "Danish",
      avatar: "👨‍💼",
      action: "created",
      timestamp: "2024-12-09 10:00",
      timeAgo: "1 day ago",
    },
  ];

  const getActionIcon = (action) => {
    switch (action) {
      case "created":
        return <Plus className="TaskHistoryTimelineSection_ClassV1_IconSm" />;
      case "updated":
        return <Edit className="TaskHistoryTimelineSection_ClassV1_IconSm" />;
      case "commented":
        return <FileText className="TaskHistoryTimelineSection_ClassV1_IconSm" />;
      case "completed":
        return <CheckCircle2 className="TaskHistoryTimelineSection_ClassV1_IconSm" />;
      case "deleted":
        return <Trash2 className="TaskHistoryTimelineSection_ClassV1_IconSm" />;
      default:
        return <Clock className="TaskHistoryTimelineSection_ClassV1_IconSm" />;
    }
  };

  const getActionVariant = (action) => {
    switch (action) {
      case "created":
        return "created";
      case "updated":
        return "updated";
      case "commented":
        return "commented";
      case "completed":
        return "completed";
      case "deleted":
        return "deleted";
      default:
        return "default";
    }
  };

  const getActionText = (entry) => {
    switch (entry.action) {
      case "created":
        return "created this task";
      case "updated":
        return `changed ${entry.field}`;
      case "commented":
        return "added a comment";
      case "completed":
        return "marked as complete";
      case "deleted":
        return "deleted this task";
      default:
        return "performed an action";
    }
  };

  const filteredEntries = useMemo(() => {
    return timelineEntries.filter((entry) => {
      if (selectedFilter === "changes") return entry.action === "updated";
      if (selectedFilter === "comments") return entry.action === "commented";
      return true;
    });
  }, [selectedFilter]);

  return (
    <section className="TaskHistoryTimelineSection_ClassV1_Section">
      <div className="TaskHistoryTimelineSection_ClassV1_Container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="TaskHistoryTimelineSection_ClassV1_Header"
        >
          <div className="TaskHistoryTimelineSection_ClassV1_Pill">
            <History className="TaskHistoryTimelineSection_ClassV1_IconMd" />
            <span className="TaskHistoryTimelineSection_ClassV1_PillText">
              Complete Audit Trail
            </span>
          </div>

          <h2 className="TaskHistoryTimelineSection_ClassV1_Title">
            Every Change. Tracked.
          </h2>

          <p className="TaskHistoryTimelineSection_ClassV1_SubTitle">
            Full transparency with a complete history of who changed what, when, and why. Never lose
            context.
          </p>
        </motion.div>

        <div className="TaskHistoryTimelineSection_ClassV1_Grid">
          {/* Timeline Visualization */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            {/* Filter Pills */}
            <div className="TaskHistoryTimelineSection_ClassV1_FilterRow">
              {[
                { id: "all", label: "All Activity" },
                { id: "changes", label: "Changes Only" },
                { id: "comments", label: "Comments Only" },
              ].map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setSelectedFilter(filter.id)}
                  className={
                    selectedFilter === filter.id
                      ? "TaskHistoryTimelineSection_ClassV1_FilterBtn TaskHistoryTimelineSection_ClassV1_FilterBtn__Active"
                      : "TaskHistoryTimelineSection_ClassV1_FilterBtn TaskHistoryTimelineSection_ClassV1_FilterBtn__Inactive"
                  }
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {/* Timeline Card */}
            <div className="TaskHistoryTimelineSection_ClassV1_TimelineCard">
              <div className="TaskHistoryTimelineSection_ClassV1_TimelineLine" />

              <div className="TaskHistoryTimelineSection_ClassV1_EntryList">
                {filteredEntries.map((entry, index) => {
                  const variant = getActionVariant(entry.action);

                  return (
                    <motion.div
                      key={entry.id}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                      className="TaskHistoryTimelineSection_ClassV1_Entry"
                    >
                      {/* Avatar + action icon */}
                      <div className="TaskHistoryTimelineSection_ClassV1_AvatarWrap">
                        <div className="TaskHistoryTimelineSection_ClassV1_Avatar">
                          {entry.avatar}
                        </div>

                        <div
                          className={`TaskHistoryTimelineSection_ClassV1_ActionDot TaskHistoryTimelineSection_ClassV1_ActionDot__${variant}`}
                        >
                          {getActionIcon(entry.action)}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="TaskHistoryTimelineSection_ClassV1_ContentCard">
                        <div className="TaskHistoryTimelineSection_ClassV1_ContentTop">
                          <div>
                            <p className="TaskHistoryTimelineSection_ClassV1_ContentTitle">
                              <span className="TaskHistoryTimelineSection_ClassV1_UserStrong">
                                {entry.user}
                              </span>{" "}
                              <span className="TaskHistoryTimelineSection_ClassV1_ActionText">
                                {getActionText(entry)}
                              </span>
                            </p>
                            <p className="TaskHistoryTimelineSection_ClassV1_TimeAgo">
                              {entry.timeAgo}
                            </p>
                          </div>
                        </div>

                        {/* Field change */}
                        {entry.field && entry.oldValue && entry.newValue && (
                          <div className="TaskHistoryTimelineSection_ClassV1_ChangeRow">
                            <span className="TaskHistoryTimelineSection_ClassV1_OldValue">
                              {entry.oldValue}
                            </span>
                            <span className="TaskHistoryTimelineSection_ClassV1_Arrow">→</span>
                            <span className="TaskHistoryTimelineSection_ClassV1_NewValue">
                              {entry.newValue}
                            </span>
                          </div>
                        )}

                        {/* Comment */}
                        {entry.comment && (
                          <div className="TaskHistoryTimelineSection_ClassV1_CommentBox">
                            <p className="TaskHistoryTimelineSection_ClassV1_CommentText">
                              “{entry.comment}”
                            </p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Benefits */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="TaskHistoryTimelineSection_ClassV1_BenefitsCol"
          >
            <div>
              <h3 className="TaskHistoryTimelineSection_ClassV1_BenefitsTitle">
                Why Task History Matters
              </h3>
              <p className="TaskHistoryTimelineSection_ClassV1_BenefitsSub">
                Never wonder what happened to a task. See every update, comment, and change with
                timestamps and user attribution.
              </p>
            </div>

            <div className="TaskHistoryTimelineSection_ClassV1_BenefitsList">
              {[
                {
                  icon: "🔍",
                  title: "Full Transparency",
                  description:
                    "See exactly who changed what, when it happened, and what the previous value was.",
                },
                {
                  icon: "⏱️",
                  title: "Time Machine",
                  description:
                    "Scroll through history to understand how a task evolved from creation to completion.",
                },
                {
                  icon: "💬",
                  title: "Inline Comments",
                  description:
                    "Comments appear right in the timeline, giving context to every change.",
                },
                {
                  icon: "🔒",
                  title: "Immutable Records",
                  description:
                    "History entries can't be edited or deleted, ensuring audit trail integrity.",
                },
                {
                  icon: "🎯",
                  title: "Filter & Search",
                  description:
                    "Find specific changes quickly with filters for updates, comments, and assignments.",
                },
                {
                  icon: "📊",
                  title: "Accountability",
                  description:
                    "Perfect for compliance, reporting, and understanding team dynamics.",
                },
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="TaskHistoryTimelineSection_ClassV1_BenefitCard"
                >
                  <div className="TaskHistoryTimelineSection_ClassV1_BenefitEmoji">
                    {benefit.icon}
                  </div>
                  <div className="TaskHistoryTimelineSection_ClassV1_BenefitBody">
                    <h4 className="TaskHistoryTimelineSection_ClassV1_BenefitTitle">
                      {benefit.title}
                    </h4>
                    <p className="TaskHistoryTimelineSection_ClassV1_BenefitDesc">
                      {benefit.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Pro Tip */}
            <div className="TaskHistoryTimelineSection_ClassV1_ProTip">
              <div className="TaskHistoryTimelineSection_ClassV1_ProTipRow">
                <div className="TaskHistoryTimelineSection_ClassV1_ProTipIconBox">
                  <History className="TaskHistoryTimelineSection_ClassV1_IconMd TaskHistoryTimelineSection_ClassV1_IconWhite" />
                </div>
                <div>
                  <h4 className="TaskHistoryTimelineSection_ClassV1_ProTipTitle">Pro Tip</h4>
                  <p className="TaskHistoryTimelineSection_ClassV1_ProTipDesc">
                    Export task history as PDF for client reports, compliance audits, or project
                    retrospectives. Every detail preserved.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}